import { createApi } from '@reduxjs/toolkit/dist/query/react';

import endpoints from '../../api/endpoints';
import { baseQueryWithReAuth } from '../../utils/baseQueryWithReAuth';
import {
  IApplicantFileResponse,
  IGridRequest,
  IGridResponse,
  IJobApplicantDetailResponse,
  IJobListing,
  IJobsAccountApplicant,
  IJobsAccountApplicantsRequest,
  IJobsAccountApplicantsResponse,
  IJobsAccountHome,
  IJobsApplicantsResponse,
  IJobsPrepaidDetailResponse,
  IJobsPrepaidResponse,
  IMyJobApplication,
  IJobApplicationNote,
  IJobHiringTeamResponse,
  IJobHiringTeamMember,
  ISavedJob,
} from './interfaces';

export const jobsAccountApi = createApi({
  reducerPath: 'serviceJobsAccount',
  tagTypes: ['JobListings', 'PrepaidPackages', 'Applicants', 'ApplicantDetail', 'HiringTeam', 'SavedJobs', 'Applications', 'AccountHome'],
  baseQuery: baseQueryWithReAuth,
  endpoints: (build) => ({
    getJobsAccountHome: build.query<IJobsAccountHome, void>({
      query: () => ({
        url: endpoints.jobs.accountHome,
      }),
      providesTags: ['JobListings', 'PrepaidPackages', 'Applicants', 'AccountHome'],
    }),
    getJobsAccountApplicants: build.query<
    IJobsAccountApplicantsResponse,
    IJobsAccountApplicantsRequest
    >({
      query: (params) => ({
        url: endpoints.jobs.accountApplicants,
        params,
      }),
      providesTags: [{ type: 'Applicants', id: 'ALL' }],
    }),
    getMyApplications: build.query<IMyJobApplication[], void>({
      query: () => ({
        url: endpoints.jobs.myApplications,
      }),
      providesTags: ['Applications'],
    }),
    getActiveJobs: build.query<IGridResponse<IJobListing>, IGridRequest>({
      query: (params) => ({
        url: endpoints.jobs.activeJobsListing,
        params,
      }),
      providesTags: ['JobListings'],
    }),
    getInactiveJobs: build.query<IGridResponse<IJobListing>, IGridRequest>({
      query: (params) => ({
        url: endpoints.jobs.inactiveJobsListing,
        params,
      }),
      providesTags: ['JobListings'],
    }),
    getJobsPrepaid: build.query<IJobsPrepaidResponse, void>({
      query: () => ({
        url: endpoints.jobs.jobsPrepaid,
      }),
      providesTags: ['PrepaidPackages'],
    }),
    getJobsPrepaidDetail: build.query<IJobsPrepaidDetailResponse, string>({
      query: (guid) => ({
        url: endpoints.jobs.jobsPrepaidDetail(guid),
      }),
      providesTags: ['PrepaidPackages'],
    }),
    getJobApplicants: build.query<IJobsApplicantsResponse, string>({
      query: (guid) => ({
        url: endpoints.jobs.jobsApplicants(guid),
      }),
      providesTags: (_result, _error, guid) => [
        'Applicants',
        { type: 'Applicants', id: guid },
      ],
    }),
    getJobApplicantDetail: build.query<
    IJobApplicantDetailResponse,
    { eguid: string; applicationId: string | number }
    >({
      query: ({ eguid, applicationId }) => ({
        url: endpoints.jobs.jobsApplicantDetail(eguid, applicationId),
      }),
      providesTags: (_result, _error, { applicationId }) => [
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    markApplicantAsRead: build.mutation<void, {
      eguid: string;
      applicationId: string | number;
    }>({
      query: ({ applicationId }) => ({
        url: endpoints.jobs.markAsReadJobsApplication(applicationId),
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, { eguid, applicationId }) => [
        { type: 'Applicants', id: eguid },
        { type: 'Applicants', id: 'ALL' },
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    getApplicantFile: build.mutation<
    IApplicantFileResponse,
    { eguid: string; fileGuid: string }
    >({
      query: ({ eguid, fileGuid }) => ({
        url: endpoints.jobs.applicantFile(eguid, fileGuid),
        method: 'GET',
      }),
      extraOptions: { suppressErrorNotification: true },
    }),
    updateApplicationStatus: build.mutation<void, {
      eguid: string;
      applicationId: string | number;
      statusId: number;
    }>({
      query: ({ applicationId, statusId }) => ({
        url: endpoints.jobs.applicationStatus(applicationId),
        method: 'PUT',
        body: { statusId },
      }),
      // The drop only awaits the save, so move the card to its new column right
      // away instead of letting it snap back until the refetch arrives. Every
      // undo runs if the save fails; the board's error alert reports the failure.
      onQueryStarted: async ({ applicationId, statusId }, { dispatch, queryFulfilled, getState }) => {
        const queries = (getState() as any)[jobsAccountApi.reducerPath]?.queries || {};
        const boardEntries: any[] = Object.values(queries)
          .filter((entry: any) => entry?.endpointName === 'getJobsAccountApplicants' && entry.originalArgs);

        // Grab the dragged applicant from whichever column currently holds it.
        let moved: IJobsAccountApplicant | undefined;
        boardEntries.forEach((entry) => {
          const found = (entry.data?.result as IJobsAccountApplicant[] | undefined)
            ?.find((row) => row.applicationId === applicationId);
          if (found) moved = found;
        });

        const patches = boardEntries.map((entry) => dispatch(
          jobsAccountApi.util.updateQueryData('getJobsAccountApplicants', entry.originalArgs, (draft) => {
            const index = draft.result.findIndex((row) => row.applicationId === applicationId);
            if (entry.originalArgs.statusId === statusId) {
              if (moved && index === -1) {
                draft.result.unshift({ ...moved, statusId });
                draft.total += 1;
              }
            } else if (index !== -1) {
              draft.result.splice(index, 1);
              draft.total = Math.max(0, draft.total - 1);
            }
          }),
        ));

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, { eguid, applicationId }) => [
        { type: 'Applicants', id: eguid },
        { type: 'Applicants', id: 'ALL' },
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    updateApplicationRating: build.mutation<void, {
      eguid: string;
      applicationId: string | number;
      rating: number | null;
    }>({
      query: ({ applicationId, rating }) => ({
        url: endpoints.jobs.applicationRating(applicationId),
        method: 'PUT',
        body: { rating },
      }),
      // Show the picked rating immediately; the control otherwise falls back to
      // the old value until the invalidated detail query refetches. Undo on a
      // failed save so the stars match the server state the error alert reports.
      onQueryStarted: async ({ eguid, applicationId, rating }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          jobsAccountApi.util.updateQueryData('getJobApplicantDetail', { eguid, applicationId }, (draft) => {
            draft.rating = rating;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { eguid, applicationId }) => [
        { type: 'Applicants', id: eguid },
        { type: 'Applicants', id: 'ALL' },
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    getApplicationNotes: build.query<IJobApplicationNote[], number | string>({
      query: (applicationId) => ({
        url: endpoints.jobs.applicationNotes(applicationId),
      }),
      providesTags: (_result, _error, applicationId) => [
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    addApplicationNote: build.mutation<IJobApplicationNote, {
      eguid: string;
      applicationId: string | number;
      note: string;
    }>({
      query: ({ applicationId, note }) => ({
        url: endpoints.jobs.applicationNotes(applicationId),
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (_result, _error, { eguid, applicationId }) => [
        { type: 'Applicants', id: eguid },
        { type: 'Applicants', id: 'ALL' },
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    deleteApplicationNote: build.mutation<void, {
      eguid: string;
      applicationId: string | number;
      noteId: number;
    }>({
      query: ({ noteId }) => ({
        url: endpoints.jobs.applicationNote(noteId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { eguid, applicationId }) => [
        { type: 'Applicants', id: eguid },
        { type: 'Applicants', id: 'ALL' },
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    requestApplicationReview: build.mutation<IJobApplicationNote, {
      eguid: string;
      applicationId: string | number;
      recipientUserId?: number;
      recipientEmail?: string;
      message?: string;
    }>({
      query: ({ applicationId, recipientUserId, recipientEmail, message }) => ({
        url: endpoints.jobs.applicationReviewRequest(applicationId),
        method: 'POST',
        body: { recipientUserId, recipientEmail, message },
      }),
      invalidatesTags: (_result, _error, { eguid, applicationId }) => [
        { type: 'Applicants', id: eguid },
        { type: 'Applicants', id: 'ALL' },
        { type: 'ApplicantDetail', id: applicationId },
        { type: 'HiringTeam', id: eguid },
      ],
    }),
    resendApplicationReview: build.mutation<void, {
      applicationId: string | number;
      noteId: number;
    }>({
      query: ({ noteId }) => ({
        url: endpoints.jobs.applicationReviewRequestResend(noteId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        { type: 'ApplicantDetail', id: applicationId },
      ],
    }),
    getJobHiringTeam: build.query<IJobHiringTeamResponse, string>({
      query: (eguid) => ({ url: endpoints.jobs.jobHiringTeam(eguid) }),
      providesTags: (_result, _error, eguid) => [{ type: 'HiringTeam', id: eguid }],
    }),
    addJobHiringTeamMember: build.mutation<IJobHiringTeamMember, { eguid: string; email: string }>({
      query: ({ eguid, email }) => ({
        url: endpoints.jobs.jobHiringTeam(eguid),
        method: 'POST',
        body: { email },
      }),
      invalidatesTags: (_result, _error, { eguid }) => [
        { type: 'HiringTeam', id: eguid },
        'AccountHome',
      ],
    }),
    removeJobHiringTeamMember: build.mutation<void, { eguid: string; userId: number }>({
      query: ({ eguid, userId }) => ({
        url: endpoints.jobs.jobHiringTeamMember(eguid, userId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { eguid }) => [
        { type: 'HiringTeam', id: eguid },
        'AccountHome',
      ],
    }),
    resendJobHiringTeamInvite: build.mutation<void, { eguid: string; userId: number }>({
      query: ({ eguid, userId }) => ({
        url: endpoints.jobs.jobHiringTeamResend(eguid, userId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { eguid }) => [{ type: 'HiringTeam', id: eguid }],
    }),
    getMySavedJobs: build.query<ISavedJob[], void>({
      query: () => ({ url: endpoints.jobs.mySavedJobs }),
      providesTags: ['SavedJobs'],
    }),
    removeSavedJob: build.mutation<void, string>({
      query: (slug) => ({
        url: endpoints.jobs.savedJob(slug),
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedJobs', 'AccountHome'],
    }),
  }),
});

export const {
  useGetJobsAccountHomeQuery,
  useGetJobsAccountApplicantsQuery,
  useGetMyApplicationsQuery,
  useGetActiveJobsQuery,
  useGetInactiveJobsQuery,
  useGetJobsPrepaidQuery,
  useGetJobsPrepaidDetailQuery,
  useGetJobApplicantsQuery,
  useGetJobApplicantDetailQuery,
  useMarkApplicantAsReadMutation,
  useGetApplicantFileMutation,
  useUpdateApplicationStatusMutation,
  useUpdateApplicationRatingMutation,
  useGetApplicationNotesQuery,
  useAddApplicationNoteMutation,
  useDeleteApplicationNoteMutation,
  useRequestApplicationReviewMutation,
  useResendApplicationReviewMutation,
  useGetJobHiringTeamQuery,
  useAddJobHiringTeamMemberMutation,
  useRemoveJobHiringTeamMemberMutation,
  useResendJobHiringTeamInviteMutation,
  useGetMySavedJobsQuery,
  useRemoveSavedJobMutation,
} = jobsAccountApi;
